import { Component, computed, effect, inject, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

import { AppRoutes } from '@gfe/app.paths';

import { AssistantWindowLink } from '@core/services/assistant/assistant-window-link';
import { AssistantService } from '@core/services/assistant/assistant.service';
import { ICONS } from '@core/services/icons/icons.service';

import { AssistantFilePreviewComponent } from '../assistant-panel/assistant-file/assistant-file-preview.component';
import { AssistantPanelComponent } from '../assistant-panel/assistant-panel.component';

/** The assistant alone in a window the Galia tab opened, linked to that tab. */
@Component({
  selector: 'app-assistant-window',
  imports: [TranslatePipe, AssistantPanelComponent, AssistantFilePreviewComponent],
  templateUrl: './assistant-window.component.html',
  host: { class: 'bg-page flex h-screen flex-col' },
  styles: ``
})
export class AssistantWindowComponent {
  protected readonly assistant = inject(AssistantService);
  protected readonly link = inject(AssistantWindowLink);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly linkId =
    inject(ActivatedRoute).snapshot.queryParamMap.get('link') ?? crypto.randomUUID();
  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(map((e) => e.lang)),
    { initialValue: this.translate.getCurrentLang() }
  );
  protected readonly ICONS = ICONS;
  protected readonly notice = computed<'other_data_space' | 'no_data_space' | null>(() => {
    const page = this.link.linked();
    if (!page) return null;
    if (page.dataSpaceId === null) return 'no_data_space';
    return page.dataSpaceId !== this.assistant.dataSpaceId() ? 'other_data_space' : null;
  });

  constructor() {
    this.link.joinAsChild(this.linkId);
    // The config only loads for an open panel, and here the panel is the whole window.
    this.assistant.panelOpen.set(true);
    const title = inject(Title);
    effect(() => {
      this.currentLang();
      title.setTitle(this.translate.instant('assistant.window_title'));
    });
    // A reply runs in the window that sent it, so the switch waits for it rather than cutting it.
    effect(() => {
      const target = this.link.linked()?.dataSpaceId ?? null;
      const own = this.assistant.dataSpaceId();
      if (
        target === null ||
        own === null ||
        target === own ||
        this.assistant.anyStreaming()
      )
        return;
      untracked(
        () =>
          void this.router.navigate(['/', AppRoutes.assistantWindow.root, 'ds', target], {
            queryParams: { link: this.linkId }
          })
      );
    });
  }

  protected openCrumb(url: string): void {
    this.link.navigate(url);
  }

  protected returnToTab(): void {
    // The tab selects the conversation the dock names at once, so it must not wait for the debounced save.
    this.assistant.saveNow();
    this.link.dock(this.assistant.activeTabId(), this.assistant.draft());
  }
}
